import React, { useState, useEffect } from 'react'
import { 
  Card, 
  Typography, 
  Row, 
  Col, 
  Tag, 
  Button, 
  Spin, 
  Alert, 
  Descriptions, 
  Timeline, 
  Space,
  Divider,
  Avatar,
  Progress,
  Statistic,
  Badge
} from 'antd'
import { 
  ArrowLeftOutlined,
  CalendarOutlined, 
  UserOutlined, 
  CarOutlined,
  DollarOutlined,
  PhoneOutlined,
  MailOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  UploadOutlined
} from '@ant-design/icons'
import { useParams, useNavigate } from 'react-router-dom'
import { customerApi } from '../services/customerApi'
import dayjs from 'dayjs'

const { Title, Text, Paragraph } = Typography

interface JobDetails {
  id: string
  jobNumber: string
  title: string
  description: string
  status: string
  priority: string
  scheduledDate: string
  estimatedDuration: number
  actualStartAt?: string
  actualEndAt?: string
  serviceAddress: string
  serviceCity: string
  serviceState: string
  serviceZip: string
  problemDescription?: string
  workPerformed?: string
  recommendations?: string
  notes?: string
  specialInstructions?: string
  accessInstructions?: string
  assignedTechnician?: {
    id: string
    firstName: string
    lastName: string
    phone: string
    email: string
    avatar?: string
  }
  vehicle?: {
    id: string
    name: string
    make: string
    model: string
    licensePlate: string
    year: number
  }
  estimate?: {
    id: string
    status: string
    option1Total: number
    option1Title?: string
    option1Description?: string
    validUntil?: string
    approvedAt?: string
    declinedAt?: string
  }
  invoices: Array<{
    id: string
    invoiceNumber: string
    status: string
    totalAmount: number
    dueAt?: string
    paidAt?: string
  }>
  business: {
    id: string
    name: string
    phone: string
    email: string
    address: string
  }
  createdAt: string
  updatedAt: string
}

const getStatusColor = (status: string) => {
  const colors = {
    'DRAFT': 'default',
    'SCHEDULED': 'blue',
    'IN_PROGRESS': 'orange',
    'COMPLETED': 'green',
    'CANCELLED': 'red',
    'ON_HOLD': 'yellow'
  }
  return colors[status] || 'default'
}

const getPriorityColor = (priority: string) => {
  const colors = {
    'LOW': 'green',
    'NORMAL': 'blue',
    'HIGH': 'orange',
    'URGENT': 'red'
  }
  return colors[priority] || 'default'
}

const getStatusProgress = (status: string) => {
  const progress = {
    'DRAFT': 10,
    'SCHEDULED': 25,
    'IN_PROGRESS': 75,
    'COMPLETED': 100,
    'CANCELLED': 0,
    'ON_HOLD': 50
  }
  return progress[status] || 0
}

export const JobDetails: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>()
  const navigate = useNavigate()
  const [job, setJob] = useState<JobDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    console.log('🔍 JobDetails: Component mounted with jobId:', jobId)
    console.log('🔍 JobDetails: Token in localStorage:', !!localStorage.getItem('customerToken'))
    console.log('🔍 JobDetails: Full token:', localStorage.getItem('customerToken'))
    
    if (jobId) {
      loadJob(jobId)
    } else {
      console.log('❌ JobDetails: No job ID provided')
    }
  }, [jobId])

  const loadJob = async (jobId: string) => {
    try {
      setLoading(true)
      setError(null)
      const jobData = await customerApi.getJob(jobId)
      setJob(jobData)
    } catch (err: any) {
      console.error('Failed to load job:', err)
      setError(err.response?.data?.message || 'Failed to load job details')
    } finally {
      setLoading(false)
    }
  }

  const handleContactTechnician = (phone?: string, email?: string) => {
    if (phone) {
      window.open(`tel:${phone}`)
    } else if (email) {
      window.open(`mailto:${email}`)
    }
  }

  const handleContactBusiness = (phone?: string, email?: string) => {
    if (phone) {
      window.open(`tel:${phone}`)
    } else if (email) {
      window.open(`mailto:${email}`)
    }
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <p style={{ marginTop: '16px' }}>Loading job details...</p>
      </div>
    )
  }

  if (error) {
    return (
      <Alert
        message="Error Loading Job"
        description={error}
        type="error"
        showIcon
        action={
          <Space>
            <Button size="small" onClick={() => navigate('/jobs')}>
              Back to Jobs
            </Button>
            <Button size="small" type="primary" onClick={() => jobId && loadJob(jobId)}>
              Retry
            </Button>
          </Space>
        }
      />
    )
  }

  if (!job) {
    return (
      <Alert
        message="Job Not Found"
        description="The requested job could not be found."
        type="warning"
        showIcon
        action={
          <Button onClick={() => navigate('/jobs')}>
            Back to Jobs
          </Button>
        }
      />
    )
  }

  return (
    <div>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
        <Col>
          <Space>
            <Button 
              icon={<ArrowLeftOutlined />} 
              onClick={() => navigate('/jobs')}
            >
              Back to Jobs
            </Button>
            <Title level={2} style={{ margin: 0 }}>
              {job.title}
            </Title>
            <Tag color={getStatusColor(job.status)}>
              {job.status.replace('_', ' ')}
            </Tag>
          </Space>
        </Col>
        <Col>
          <Space>
            <Badge count={job.invoices.length} showZero>
              <Button>Invoices</Button>
            </Badge>
            {job.assignedTechnician && (
              <Button 
                type="primary"
                onClick={() => handleContactTechnician(job.assignedTechnician?.phone, job.assignedTechnician?.email)}
              >
                Contact Technician
              </Button>
            )}
          </Space>
        </Col>
      </Row>

      {/* Progress Bar */}
      <Card style={{ marginBottom: '24px' }}>
        <Row align="middle" gutter={16}>
          <Col flex="auto">
            <Progress 
              percent={getStatusProgress(job.status)} 
              status={job.status === 'CANCELLED' ? 'exception' : 'active'}
              strokeColor={job.status === 'COMPLETED' ? '#52c41a' : undefined}
            />
          </Col>
          <Col>
            <Text strong>Job #{job.jobNumber}</Text>
          </Col>
        </Row>
      </Card>

      <Row gutter={[24, 24]}>
        {/* Left Column */}
        <Col xs={24} lg={16}>
          {/* Job Information */}
          <Card title="Job Information" style={{ marginBottom: '24px' }}>
            <Descriptions column={1}>
              <Descriptions.Item label="Description">
                <Paragraph>{job.description}</Paragraph>
              </Descriptions.Item>
              
              {job.problemDescription && (
                <Descriptions.Item label="Problem Description">
                  <Paragraph>{job.problemDescription}</Paragraph>
                </Descriptions.Item>
              )}

              {job.workPerformed && (
                <Descriptions.Item label="Work Performed">
                  <Paragraph>{job.workPerformed}</Paragraph>
                </Descriptions.Item>
              )}

              {job.recommendations && (
                <Descriptions.Item label="Recommendations">
                  <Paragraph>{job.recommendations}</Paragraph>
                </Descriptions.Item>
              )}

              {job.notes && (
                <Descriptions.Item label="Notes">
                  <Paragraph>{job.notes}</Paragraph>
                </Descriptions.Item>
              )}

              <Descriptions.Item label="Priority">
                <Tag color={getPriorityColor(job.priority)}>
                  {job.priority} Priority
                </Tag>
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Schedule & Timeline */}
          <Card title="Schedule & Timeline" style={{ marginBottom: '24px' }}>
            <Timeline>
              <Timeline.Item 
                dot={<CalendarOutlined />} 
                color="blue"
              >
                <Text strong>Scheduled</Text>
                <br />
                {dayjs(job.scheduledDate).format('MMMM D, YYYY h:mm A')}
                {job.estimatedDuration && (
                  <Text type="secondary"> • Est. {job.estimatedDuration} hours</Text>
                )}
              </Timeline.Item>

              {job.actualStartAt && (
                <Timeline.Item 
                  dot={<ClockCircleOutlined />} 
                  color="orange"
                >
                  <Text strong>Started</Text>
                  <br />
                  {dayjs(job.actualStartAt).format('MMMM D, YYYY h:mm A')}
                </Timeline.Item>
              )}

              {job.actualEndAt && (
                <Timeline.Item 
                  dot={<CheckCircleOutlined />} 
                  color="green"
                >
                  <Text strong>Completed</Text>
                  <br />
                  {dayjs(job.actualEndAt).format('MMMM D, YYYY h:mm A')}
                </Timeline.Item>
              )}

              {job.status === 'CANCELLED' && (
                <Timeline.Item 
                  dot={<ExclamationCircleOutlined />} 
                  color="red"
                >
                  <Text strong>Cancelled</Text>
                </Timeline.Item>
              )}
            </Timeline>
          </Card>

          {/* Instructions */}
          {(job.specialInstructions || job.accessInstructions) && (
            <Card title="Instructions" style={{ marginBottom: '24px' }}>
              {job.specialInstructions && (
                <div style={{ marginBottom: '16px' }}>
                  <Text strong>Special Instructions:</Text>
                  <Paragraph>{job.specialInstructions}</Paragraph>
                </div>
              )}
              
              {job.accessInstructions && (
                <div>
                  <Text strong>Access Instructions:</Text>
                  <Paragraph>{job.accessInstructions}</Paragraph>
                </div>
              )}
            </Card>
          )}

          {/* Document Upload Section */}
          <Card title="Upload Documents" style={{ marginBottom: '24px' }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text type="secondary">
                Upload photos, receipts, or other documents related to this job.
              </Text>
              <Button 
                type="primary"
                icon={<UploadOutlined />}
                onClick={() => navigate(`/jobs/${jobId}/documents`)}
              >
                Upload Documents
              </Button>
              
              {/* TODO: Show existing documents list here */}
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Supported formats: JPG, PNG, PDF, DOC, DOCX (Max 10MB per file)
              </Text>
            </Space>
          </Card>
        </Col>

        {/* Right Column */}
        <Col xs={24} lg={8}>
          {/* Service Location */}
          <Card title="Service Location" style={{ marginBottom: '24px' }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <EnvironmentOutlined style={{ marginRight: '8px' }} />
                <Text strong>Address</Text>
              </div>
              <Paragraph>
                {job.serviceAddress}<br />
                {job.serviceCity}, {job.serviceState} {job.serviceZip}
              </Paragraph>
            </Space>
          </Card>

          {/* Assigned Technician */}
          {job.assignedTechnician && (
            <Card title="Assigned Technician" style={{ marginBottom: '24px' }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Space>
                  <Avatar 
                    src={job.assignedTechnician.avatar} 
                    icon={<UserOutlined />}
                    size="large"
                  />
                  <div>
                    <Text strong>
                      {job.assignedTechnician.firstName} {job.assignedTechnician.lastName}
                    </Text>
                  </div>
                </Space>
                
                <Space direction="vertical" size="small">
                  {job.assignedTechnician.phone && (
                    <Button 
                      type="link" 
                      icon={<PhoneOutlined />}
                      onClick={() => handleContactTechnician(job.assignedTechnician?.phone)}
                      style={{ padding: 0 }}
                    >
                      {job.assignedTechnician.phone}
                    </Button>
                  )}
                  
                  {job.assignedTechnician.email && (
                    <Button 
                      type="link" 
                      icon={<MailOutlined />}
                      onClick={() => handleContactTechnician(undefined, job.assignedTechnician?.email)}
                      style={{ padding: 0 }}
                    >
                      {job.assignedTechnician.email}
                    </Button>
                  )}
                </Space>
              </Space>
            </Card>
          )}

          {/* Vehicle */}
          {job.vehicle && (
            <Card title="Service Vehicle" style={{ marginBottom: '24px' }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <CarOutlined style={{ marginRight: '8px' }} />
                  <Text strong>{job.vehicle.name}</Text>
                </div>
                <Text>
                  {job.vehicle.year} {job.vehicle.make} {job.vehicle.model}
                </Text>
                {job.vehicle.licensePlate && (
                  <Text type="secondary">
                    License: {job.vehicle.licensePlate}
                  </Text>
                )}
              </Space>
            </Card>
          )}

          {/* Estimate */}
          {job.estimate && (
            <Card title="Estimate" style={{ marginBottom: '24px' }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Statistic
                  title="Amount"
                  value={job.estimate.option1Total}
                  precision={2}
                  prefix={<DollarOutlined />}
                />
                
                <Tag color={job.estimate.status === 'APPROVED' ? 'green' : 'blue'}>
                  {job.estimate.status}
                </Tag>

                {job.estimate.option1Title && (
                  <div>
                    <Text strong>{job.estimate.option1Title}</Text>
                    {job.estimate.option1Description && (
                      <Paragraph>{job.estimate.option1Description}</Paragraph>
                    )}
                  </div>
                )}

                {job.estimate.validUntil && (
                  <Text type="secondary">
                    Valid until: {dayjs(job.estimate.validUntil).format('MMM D, YYYY')}
                  </Text>
                )}
              </Space>
            </Card>
          )}

          {/* Invoices */}
          {job.invoices.length > 0 && (
            <Card title="Invoices" style={{ marginBottom: '24px' }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                {job.invoices.map((invoice) => (
                  <Card key={invoice.id} size="small">
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      <div>
                        <Text strong>#{invoice.invoiceNumber}</Text>
                        <Tag 
                          color={invoice.status === 'PAID' ? 'green' : 'orange'}
                          style={{ float: 'right' }}
                        >
                          {invoice.status}
                        </Tag>
                      </div>
                      
                      <Statistic
                        value={invoice.totalAmount}
                        precision={2}
                        prefix="$"
                        size="small"
                      />

                      {invoice.dueAt && !invoice.paidAt && (
                        <Text type="secondary">
                          Due: {dayjs(invoice.dueAt).format('MMM D, YYYY')}
                        </Text>
                      )}

                      {invoice.paidAt && (
                        <Text type="success">
                          Paid: {dayjs(invoice.paidAt).format('MMM D, YYYY')}
                        </Text>
                      )}
                    </Space>
                  </Card>
                ))}
              </Space>
            </Card>
          )}

          {/* Business Contact */}
          <Card title="Business Contact" style={{ marginBottom: '24px' }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text strong>{job.business.name}</Text>
              
              <Space direction="vertical" size="small">
                {job.business.phone && (
                  <Button 
                    type="link" 
                    icon={<PhoneOutlined />}
                    onClick={() => handleContactBusiness(job.business.phone)}
                    style={{ padding: 0 }}
                  >
                    {job.business.phone}
                  </Button>
                )}
                
                {job.business.email && (
                  <Button 
                    type="link" 
                    icon={<MailOutlined />}
                    onClick={() => handleContactBusiness(undefined, job.business.email)}
                    style={{ padding: 0 }}
                  >
                    {job.business.email}
                  </Button>
                )}
              </Space>

              {job.business.address && (
                <Text type="secondary">{job.business.address}</Text>
              )}
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  )
}
